import React, { useState, useEffect, useCallback } from 'react';
import { userRoleApi, companyApi, AccessLevel, Company } from '../../utils/api';
import { X } from 'lucide-react';
import { useAuth } from '../../lib/security';
import CompaniesDialog from './CompaniesDialog';

interface UserRole {
    id: string;
    userPrincipalName: string;
    name: string;
    objectId: string | null;
    /** Superseded by the per-area levels; unread. */
    role: string;
    releaseNotesLevel: AccessLevel;
    wikiLevel: AccessLevel;
    isSystemAdmin: boolean;
    /** The customer this person belongs to, or null for Binary staff. */
    companyId: string | null;
    companyName: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

/**
 * Who has access to BIKI, at what level, and on whose behalf.
 *
 * Was the top half of the admin page, beside the image gallery. They answered
 * unrelated questions and only shared a page because both needed system
 * administration, so they are now a tab each.
 */
const UsersTab: React.FC = () => {
    const { userInfo } = useAuth();
    const [roles, setRoles] = useState<UserRole[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignUpn, setAssignUpn] = useState('');
    const [assignReleaseNotes, setAssignReleaseNotes] = useState<AccessLevel>('Reader');
    const [assignWiki, setAssignWiki] = useState<AccessLevel>('Reader');
    const [assignSystemAdmin, setAssignSystemAdmin] = useState(false);
    // '' is the internal case - Binary staff, who belong to no customer - and
    // is a real choice rather than an empty field, so the select always has it.
    const [assignCompanyId, setAssignCompanyId] = useState('');
    const [assignResult, setAssignResult] = useState<string | null>(null);
    // The UPN being edited, or null when granting access to someone new.
    // Assignment is an upsert, so editing is the same call with the fields
    // pre-filled and the address locked.
    const [editingUpn, setEditingUpn] = useState<string | null>(null);

    const [companies, setCompanies] = useState<Company[]>([]);
    const [showCompanies, setShowCompanies] = useState(false);

    const fetchRoles = useCallback(async () => {
        setLoading(true);
        setError(null);
        const result = await userRoleApi.getAll();
        if (result.error) {
            setError(result.error);
            setRoles([]);
        } else {
            setRoles(result.data);
        }
        setLoading(false);
    }, []);

    // Kept beside the users rather than fetched when the dialog opens: the
    // table names each person's company on every row, and the counts shown in
    // the companies dialog have to agree with what the table says.
    const fetchCompanies = useCallback(async () => {
        const result = await companyApi.getAll();
        if (result.error) {
            setError(result.error);
        } else {
            setCompanies(result.data);
        }
    }, []);

    useEffect(() => {
        fetchRoles();
        fetchCompanies();
    }, [fetchRoles, fetchCompanies]);

    /**
     * Losing the last system administrator means nobody can grant access again,
     * recoverable only by editing the database by hand. The API refuses it; the
     * UI declines to offer it.
     */
    const systemAdmins = roles.filter((r) => r.isSystemAdmin && r.isActive);
    const isOnlySystemAdmin = (role: UserRole) =>
        role.isSystemAdmin && role.isActive && systemAdmins.length <= 1;
    const editingOnlySystemAdmin =
        !!editingUpn && systemAdmins.length <= 1 &&
        systemAdmins.some((r) => r.userPrincipalName === editingUpn);

    const openGrant = () => {
        setEditingUpn(null);
        setAssignUpn('');
        setAssignReleaseNotes('Reader');
        setAssignWiki('Reader');
        setAssignSystemAdmin(false);
        setAssignCompanyId('');
        setAssignResult(null);
        setShowAssignModal(true);
    };

    const openEdit = (role: UserRole) => {
        setEditingUpn(role.userPrincipalName);
        setAssignUpn(role.userPrincipalName);
        setAssignReleaseNotes(role.releaseNotesLevel ?? 'None');
        setAssignWiki(role.wikiLevel ?? 'None');
        setAssignSystemAdmin(role.isSystemAdmin ?? false);
        setAssignCompanyId(role.companyId ?? '');
        setAssignResult(null);
        setShowAssignModal(true);
    };

    /**
     * A system administrator can grant themselves anything, so withholding an
     * area from them is not a real restriction - it just misrepresents what
     * they can do. Ticking it therefore sets both areas to Admin and locks them.
     *
     * The company is deliberately left alone. It says who somebody is, not what
     * they may do, and a customer's own administrator is a coherent thing to
     * be - they still see only their own release notes.
     */
    const handleSystemAdminChange = (checked: boolean) => {
        setAssignSystemAdmin(checked);
        if (checked) {
            setAssignReleaseNotes('Admin');
            setAssignWiki('Admin');
        }
    };

    const handleAssign = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!assignUpn.trim()) return;

        const result = await userRoleApi.assign({
            userPrincipalName: assignUpn.trim(),
            releaseNotes: assignReleaseNotes,
            wiki: assignWiki,
            isSystemAdmin: assignSystemAdmin,
            companyId: assignCompanyId || null,
        });

        if (result.error) {
            // Stay open on failure: the dialog holds the values that need fixing.
            setAssignResult(`Error: ${result.error}`);
            return;
        }

        // Saved. Close, and let the refreshed row be the confirmation - a
        // message inside a dialog nobody is looking at any more is not one.
        setShowAssignModal(false);
        setAssignResult(null);
        setEditingUpn(null);
        setAssignUpn('');
        setAssignReleaseNotes('Reader');
        setAssignWiki('Reader');
        setAssignSystemAdmin(false);
        setAssignCompanyId('');
        fetchRoles();
        // The companies dialog reports how many people belong to each one, and
        // this assignment has just changed that.
        fetchCompanies();
    };

    const handleRemove = async (upn: string) => {
        if (!window.confirm(`Remove role from ${upn}?`)) return;

        const result = await userRoleApi.remove(upn);
        if (result.error) {
            setError(result.error);
        } else {
            fetchRoles();
            fetchCompanies();
        }
    };

    const getRoleBadgeClass = (level: string) => {
        switch (level) {
            case 'Admin':
                return 'bg-red-100 text-red-800';
            case 'Contributor':
                return 'bg-amber-100 text-amber-800';
            case 'Reader':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-slate-100 text-slate-500';
        }
    };

    /** One badge per area, so a row reads as the whole of someone's access. */
    const renderLevel = (label: string, level: string) => (
        <span className="inline-flex items-center gap-1.5">
            <span className="text-xs text-slate-400 w-10">{label}</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass(level)}`}>
                {level}
            </span>
        </span>
    );

    return (
        <div className="fade-in">
            {/* Actions */}
            <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-slate-500">
                    {loading ? 'Loading…' : `${roles.length} user${roles.length === 1 ? '' : 's'}`}
                </p>
                <div className="flex gap-3">
                    <button
                        onClick={() => setShowCompanies(true)}
                        className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium"
                    >
                        Manage companies
                    </button>
                    <button
                        onClick={openGrant}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                        + Grant Access
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                </div>
            )}

            {/* Info card */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
                <div className="flex items-start gap-3">
                    <span className="text-xl">⚠️</span>
                    <div>
                        <h3 className="font-semibold text-amber-800">Permission Management</h3>
                        <p className="text-sm text-amber-700 mt-1">
                            Users with <strong>No Role</strong> assigned will be denied access to the application.
                            Assign roles carefully. The current user is <strong>{userInfo?.email}</strong>.
                        </p>
                        <p className="text-sm text-amber-700 mt-2">
                            A user's <strong>company</strong> decides which release notes they see: only
                            those targeted at their company, plus notes targeted at nobody in particular.
                            Leave it as <strong>Binary (internal)</strong> for staff, who see everything.
                        </p>
                    </div>
                </div>
            </div>

            {/* Users table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800">Users</h3>
                </div>
                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="spinner"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-slate-50">
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        User
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        Company
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        Role
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        Last Updated
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {roles.map((role) => (
                                    <tr key={role.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div>
                                                <p className="text-sm font-medium text-slate-800">{role.userPrincipalName}</p>
                                                {role.name && (
                                                    <p className="text-xs text-slate-400 font-mono">{role.name}</p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {/* No company is the internal case, not a gap, so it is
                                                named rather than left as a dash. */}
                                            {role.companyName ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                                                    {role.companyName}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-slate-400">Binary (internal)</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1 items-start">
                                                {/* A system administrator is Admin of both areas by
                                                    definition, so listing them again says nothing. */}
                                                {role.isSystemAdmin ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                        System admin
                                                    </span>
                                                ) : (
                                                    <>
                                                        {renderLevel('Notes', role.releaseNotesLevel ?? 'None')}
                                                        {renderLevel('Docs', role.wikiLevel ?? 'None')}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`text-xs ${role.isActive ? 'text-green-600' : 'text-slate-400'}`}>
                                                {role.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-500">
                                            {new Date(role.updatedAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => openEdit(role)}
                                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline mr-4"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleRemove(role.userPrincipalName)}
                                                disabled={isOnlySystemAdmin(role)}
                                                title={isOnlySystemAdmin(role)
                                                    ? 'The only system administrator cannot be removed. Grant system administration to somebody else first.'
                                                    : undefined}
                                                className="text-sm text-red-500 hover:text-red-700 hover:underline disabled:text-slate-300 disabled:no-underline disabled:cursor-not-allowed"
                                            >
                                                Remove
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {roles.length === 0 && (
                            <div className="text-center py-8 text-slate-400">
                                No roles assigned yet.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Companies */}
            {showCompanies && (
                <CompaniesDialog
                    companies={companies}
                    onClose={() => setShowCompanies(false)}
                    onChanged={() => {
                        fetchCompanies();
                        // A rename shows on every user row that names it.
                        fetchRoles();
                    }}
                />
            )}

            {/* Assign Role Modal */}
            {showAssignModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 fade-in">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-slate-800">
                                {editingUpn ? 'Edit access' : 'Grant access'}
                            </h2>
                            <button onClick={() => setShowAssignModal(false)} className="text-slate-400 hover:text-slate-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleAssign} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    User Email (UPN) *
                                </label>
                                {/* Locked while editing: changing the address would create a
                                    second row rather than rename this one. */}
                                <input
                                    type="email"
                                    value={assignUpn}
                                    onChange={(e) => setAssignUpn(e.target.value)}
                                    placeholder="user@company.com"
                                    disabled={!!editingUpn}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-slate-100 disabled:text-slate-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    Company
                                </label>
                                <select
                                    value={assignCompanyId}
                                    onChange={(e) => setAssignCompanyId(e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                >
                                    <option value="">Binary (internal) — sees every release note</option>
                                    {companies.map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-slate-400 mt-1">
                                    {assignCompanyId
                                        ? 'Sees release notes targeted at this company, and notes targeted at nobody in particular.'
                                        : 'No company means Binary staff. Give customers a company so they do not see other customers’ release notes.'}
                                </p>
                                {companies.length === 0 && (
                                    <p className="text-xs text-amber-600 mt-1">
                                        No companies yet — open “Manage companies” and use
                                        “Refresh from Jira”.
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    Release Notes
                                </label>
                                <select
                                    value={assignReleaseNotes}
                                    onChange={(e) => setAssignReleaseNotes(e.target.value as AccessLevel)}
                                    disabled={assignSystemAdmin}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-slate-100 disabled:text-slate-500"
                                >
                                    <option value="None">No access</option>
                                    <option value="Reader">Reader (read only)</option>
                                    <option value="Contributor">Contributor (create and edit)</option>
                                    <option value="Admin">Admin (also delete)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    Documentation
                                </label>
                                <select
                                    value={assignWiki}
                                    onChange={(e) => setAssignWiki(e.target.value as AccessLevel)}
                                    disabled={assignSystemAdmin}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-slate-100 disabled:text-slate-500"
                                >
                                    <option value="None">No access</option>
                                    <option value="Reader">Reader (published articles only)</option>
                                    <option value="Contributor">Contributor (write, sees drafts)</option>
                                    <option value="Admin">Admin (also delete and manage sections)</option>
                                </select>
                            </div>
                            <label className="flex items-start gap-2">
                                <input
                                    type="checkbox"
                                    checked={assignSystemAdmin}
                                    onChange={(e) => handleSystemAdminChange(e.target.checked)}
                                    disabled={editingOnlySystemAdmin}
                                    className="mt-0.5 disabled:cursor-not-allowed"
                                />
                                <span className="text-sm text-slate-700">
                                    System administrator
                                    <span className="block text-xs text-slate-400">
                                        {editingOnlySystemAdmin
                                            ? 'The only system administrator. Grant this to somebody else before removing it here.'
                                            : assignSystemAdmin
                                            ? 'Manages who has access, and administers both areas.'
                                            : 'Manages who has access. Grants Admin on both areas.'}
                                    </span>
                                </span>
                            </label>
                            {assignResult && (
                                <div className={`p-3 rounded-lg text-sm ${
                                    assignResult.startsWith('Error')
                                        ? 'bg-red-50 text-red-700'
                                        : 'bg-green-50 text-green-700'
                                }`}>
                                    {assignResult}
                                </div>
                            )}
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowAssignModal(false)}
                                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                >
                                    {editingUpn ? 'Save' : 'Grant'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersTab;
